// SensitivityTornado — Tornado chart for sensitivity analysis
// Shows which variables have the highest impact on the target metric
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import { semanticColors } from '../../../theme/theme';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import type { SensitivityVariableResult } from '../../../services/bomIntelligenceService';

interface SensitivityTornadoProps {
  variables: SensitivityVariableResult[];
  targetMetric: 'TOTAL_COST' | 'READINESS_SCORE' | 'AVG_LEAD_TIME';
  baselineValue: number;
}

const metricLabels: Record<string, string> = {
  TOTAL_COST: 'Total Cost',
  READINESS_SCORE: 'Readiness Score',
  AVG_LEAD_TIME: 'Avg Lead Time',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatMetricValue = (value: number, metric: string) => {
  if (metric === 'TOTAL_COST') return formatCurrency(value);
  if (metric === 'READINESS_SCORE') return `${value.toFixed(1)}%`;
  return `${value.toFixed(1)}d`;
};

export const SensitivityTornado: React.FC<SensitivityTornadoProps> = ({
  variables,
  targetMetric,
  baselineValue,
}) => {
  if (!variables || variables.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No sensitivity data available. Run a sensitivity analysis to see which variables have the highest impact.
        </Typography>
      </Paper>
    );
  }

  // Build chart data: sorted by impact magnitude (largest at top)
  const getImpact = (v: SensitivityVariableResult) =>
    targetMetric === 'READINESS_SCORE'
      ? v.impactOnReadiness
      : v.impactOnCost;

  const chartData = variables
    .sort((a, b) => {
      const impA = getImpact(a);
      const impB = getImpact(b);
      return (impB.max - impB.min) - (impA.max - impA.min);
    })
    .map((v) => {
      const impact = getImpact(v);
      return {
        name: v.variableName,
        low: impact.min - baselineValue,
        high: impact.max - baselineValue,
        lowValue: impact.min,
        highValue: impact.max,
        baseline: baselineValue,
        range: impact.max - impact.min,
      };
    });

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        Sensitivity Analysis — {metricLabels[targetMetric] || targetMetric}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each bar shows the impact range when varying the parameter by -20% to +20%.
        Longer bars indicate higher sensitivity. Baseline: {formatMetricValue(baselineValue, targetMetric)}
      </Typography>

      <Box sx={{ width: '100%', height: Math.max(200, chartData.length * 50 + 60) }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 40, bottom: 5, left: 120 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              tickFormatter={(v: number) =>
                targetMetric === 'TOTAL_COST'
                  ? `${v >= 0 ? '+' : ''}$${Math.abs(v).toFixed(0)}`
                  : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`
              }
            />
            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
            <ReferenceLine x={0} stroke="#666" strokeWidth={2} />
            <RechartsTooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <Paper sx={{ p: 1.5, maxWidth: 250 }}>
                    <Typography variant="subtitle2">{d.name}</Typography>
                    <Typography variant="body2">
                      Low (-20%): {formatMetricValue(d.lowValue, targetMetric)}
                    </Typography>
                    <Typography variant="body2">
                      High (+20%): {formatMetricValue(d.highValue, targetMetric)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      Impact Range: {formatMetricValue(d.range, targetMetric === 'TOTAL_COST' ? 'TOTAL_COST' : 'READINESS_SCORE')}
                    </Typography>
                  </Paper>
                );
              }}
            />
            {/* Negative impact (cost decrease / readiness decrease) */}
            <Bar dataKey="low" stackId="tornado" name="Decrease">
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.low < 0 ? semanticColors.status.successDark : semanticColors.status.errorDark} />
              ))}
            </Bar>
            {/* Positive impact (cost increase / readiness increase) */}
            <Bar dataKey="high" stackId="tornado" name="Increase">
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={entry.high > 0 ? semanticColors.status.errorDark : semanticColors.status.successDark} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default SensitivityTornado;
